import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, TextField, Stack, CircularProgress, Alert, Chip, InputAdornment,
  Table, TableBody, TableCell, TableHead, TableRow, Switch, FormControlLabel,
  LinearProgress, Tooltip, Accordion, AccordionSummary, AccordionDetails, Button,
  IconButton, ToggleButton, ToggleButtonGroup, Select, MenuItem, FormControl, InputLabel,
  Collapse,
} from '@mui/material';
import BalanceRounded from '@mui/icons-material/BalanceRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';

import {
  getInkStock, getInkDemandSummary, getJobInkDemand, getInkOnOrder,
  InkStockReport, InkStockRow, InkDemandSummary, InkDemandJob,
} from './productionApi';
import { Swatch, SeriesTag, DetailDialog, AdjustDialog, LinesDialog } from './InkStockView';
import { productionKeys } from './queryKeys';
import { serial } from './board/useArtworkSeparations';
import { TOKENS } from './board/ganttConfig';

const LMQ_RED = '#d32f2f';
const TROLLEY_BLUE = '#2563eb';   // stock out on a trolley — still ours

const kg = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg`;

const money = (v: number | null | undefined) =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * One SKU's balance line: what we hold, what's coming, what's needed, what's left.
 *   balance = store + on_order − outstanding demand
 *
 * where outstanding = gross demand − what is already kitted to an open trolley
 * run. Trolley stock is deliberately NOT in the cover: it is committed to the
 * run whose demand has just been netted off, so counting it would credit the
 * same tubs twice. Netting both sides together is what keeps the number steady
 * from kit through to reconcile (Jack, 11/09 — before this, a kitted job went
 * on demanding its full weight while the tubs covering it drained out of stock,
 * and the balance went negative).
 * `shortfall` is how far the projected balance sits BELOW the reorder point
 * (positive = needs ordering); it's the sort key so the order list floats up.
 */
interface BalanceRow {
  ink_key: string;
  description: string;
  colour_kind: string | null;
  hex_colour: string | null;
  material_class: string;
  series_suffix: string;
  series_name: string;
  manufacturer: string;
  ink_system: string;
  series_hex: string | null;
  low_migration: boolean;
  supplier_code: string | null;
  reorder_point_kg: number | null;
  store_kg: number;
  trolley_kg: number;       // out on trolleys — still ours
  on_hand_kg: number;
  stock_value: number;
  on_order_kg: number;
  demand_kg: number;        // OUTSTANDING: gross − allocated, floored at 0
  gross_demand_kg: number;  // what the open jobs need before anything is kitted
  allocated_kg: number;     // already on an open trolley run for it
  balance_kg: number;
  shortfall: number;
  in_stock_master: boolean; // false = demand assigned to an ink not in the stock list
  src: InkStockRow;         // the underlying stock row — passed intact to the Adjust dialog
}

const CONSIGN_COLOUR = '#9a6a00';   // consigned = supplier-owned until we use it

/** Under the on-hand kg: the consigned (supplier's) vs owned (ours) split. A SKU
 *  often holds both — a consigned delivery plus a spot-purchased top-up. */
function OwnerSplit({ consigned, owned }: { consigned?: number; owned?: number }) {
  const c = consigned ?? 0;
  const o = owned ?? 0;
  if (c <= 0 && o <= 0) return null;
  const fmt = (n: number) => (n % 1 ? n.toFixed(1) : String(n));
  if (c > 0 && o > 0) {
    return (
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: TOKENS.ink3, lineHeight: 1.1 }}>
        <Box component="span" sx={{ color: CONSIGN_COLOUR, fontWeight: 700 }}>{fmt(c)} cons</Box>
        {' · '}{fmt(o)} own
      </Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
      color: c > 0 ? CONSIGN_COLOUR : TOKENS.ink3, lineHeight: 1.1 }}>
      {c > 0 ? 'CONSIGNED' : 'OWNED'}
    </Typography>
  );
}

/**
 * InkBalanceView — the ordering-decision report: Stock-on-Hand and Demand merged
 * per ink SKU into `On Hand + On Order − Demand = Balance`.
 *
 * Reuses the two existing endpoints as-is (both key by ink_key, both in kg):
 *   - GET /production/ink-stock          → on hand + reorder point (per SKU)
 *   - GET /production/ink-demand/summary  → demand (per assigned ink; CACHE-ONLY)
 * The demand endpoint is cache-only, so this view warms not-yet-cached artworks
 * one-at-a-time through the shared serial() queue, exactly like InkOrderView —
 * never bursting wave.abaris.com.au. The mileage knob is the house default for
 * un-overridden separations; per-separation overrides (Ink Assign) are absolute,
 * so the roll-up is computed server-side at the chosen mileage (debounced).
 *
 * ON ORDER is present but always 0 today: there's no ink-PO source yet (mission
 * brief Phase 5). The column stays so the report's shape is final; it lights up
 * when PO issuance lands. Demand with no ink assigned (ink_key = null) can't be
 * netted against a SKU, so it's surfaced separately as work outstanding.
 */
export default function InkBalanceView({ embedded }: { embedded?: boolean } = {}) {
  const queryClient = useQueryClient();

  // Mileage is NOT a knob on this screen any more (Jack, 09/09), and — more
  // importantly — this screen no longer SENDS one.
  //
  // An explicit ?mileage= makes the backend apply that rate FLAT to every
  // separation; omitting it lets compute_ink_demand use its per-colour class
  // defaults (process/ECG at 1.2 g/m², Pantone and brand spots at 1.5). The old
  // knob defaulted to 1.5 and always sent it, so this screen quoted process
  // colours ~25% heavier than the Ink Sticker did for the same job — the
  // sticker is scan-triggered and never sends the param. Omitting it here is
  // what makes balance, trolley demand and the sticker agree.

  const [search, setSearch] = useState('');
  const [shortfallOnly, setShortfallOnly] = useState(false);
  const [materialClass, setMaterialClass] = useState<'ink' | 'coating' | 'all'>('all');
  const [seriesFilter, setSeriesFilter] = useState<string>('all');   // series_suffix, or 'all'
  const [expanded, setExpanded] = useState<Set<string>>(new Set());  // ink_keys drilled open

  const toggleExpanded = (inkKey: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(inkKey)) next.delete(inkKey); else next.add(inkKey);
    return next;
  });

  // The stock ledger modal + entry dialogs, reused as-is from the stock screen.
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [adjustRow, setAdjustRow] = useState<InkStockRow | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);

  // Stock: filtered by the Inks/Coatings toggle (own param set → own cache slot).
  const stockParams = { materialClass, search: '', belowReorder: false };
  const stockQ = useQuery<InkStockReport>({
    queryKey: productionKeys.inkStock(stockParams),
    queryFn: () => getInkStock(stockParams),
    staleTime: 30 * 1000,
  });

  /** A receipt / opening / adjustment moves the roll-up, the batches AND every detail. */
  const invalidateLedger = () => {
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-stock'] });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-batches'] });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-ledger'] });
  };

  // Demand: computed server-side at the house class defaults, so per-separation
  // overrides (absolute g/m²) are honoured and the rest use the knob as default.
  // The kg come back final — no client-side scaling.
  const demandQ = useQuery<InkDemandSummary>({
    queryKey: productionKeys.inkDemandSummary(),
    queryFn: () => getInkDemandSummary(),
    staleTime: 5 * 60 * 1000,
  });
  const demand = demandQ.data;

  // On order: outstanding kg per SKU on issued POs (empty until POs are placed).
  const onOrderQ = useQuery<Record<string, number>>({
    queryKey: productionKeys.inkOnOrder(),
    queryFn: getInkOnOrder,
    staleTime: 30 * 1000,
  });
  const onOrderByInk = onOrderQ.data ?? {};

  // --- PASSIVE demand: read the shared separation_extractions cache only. ---
  // Demand/coverage is persisted per artwork in production.separation_extractions
  // (keyed by pdf_path) the first time it's extracted — by the board, stickers,
  // the Demand tab, or the button below. So this ordering screen never sweeps
  // Cloudflow on its own; it shows what's already cached and lets you top up the
  // stragglers explicitly. Warming is one-at-a-time through the shared serial
  // queue, and each queued call is skipped once we unmount (cancelled ref).
  const attempted = useRef<Set<string>>(new Set());
  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);
  const [warm, setWarm] = useState<{ total: number; done: number }>({ total: 0, done: 0 });

  const warmPending = () => {
    const pending = demand?.pending_extraction ?? [];
    const fresh = pending.filter((j) => !attempted.current.has(j.job_number));
    if (fresh.length === 0) return;
    fresh.forEach((j) => attempted.current.add(j.job_number));
    setWarm((w) => ({ total: w.total + fresh.length, done: w.done }));
    const bump = () => { if (!cancelled.current) setWarm((w) => ({ ...w, done: w.done + 1 })); };
    Promise.allSettled(
      fresh.map((j) => serial(() =>
        cancelled.current ? Promise.resolve(null) : getJobInkDemand(j.job_number, 1),
      ).then(bump, bump)),
    ).then(() => {
      if (!cancelled.current) queryClient.invalidateQueries({ queryKey: productionKeys.inkDemandSummary() });
    });
  };

  // Demand per assigned ink (already at the fetched mileage), the contributing
  // jobs per ink, plus the unassignable remainder. No client-side scaling — the
  // server applied the mileage (and any per-separation overrides).
  const { demandByInk, allocByInk, jobsByInk, unassignedKg } = useMemo(() => {
    const map = new Map<string, number>();
    const alloc = new Map<string, number>();
    const jobs = new Map<string, InkDemandJob[]>();
    let unassigned = 0;
    (demand?.inks ?? []).forEach((r) => {
      if (r.ink_key) {
        map.set(r.ink_key, (map.get(r.ink_key) ?? 0) + r.total_kg);
        // allocated_kg is already a per-INK total from the server (it counts the
        // tubs on open runs, not per job), so take it, don't accumulate it —
        // two demand rows for the same ink would otherwise double it.
        alloc.set(r.ink_key, r.allocated_kg ?? 0);
        if (r.jobs?.length) jobs.set(r.ink_key, [...(jobs.get(r.ink_key) ?? []), ...r.jobs]);
      } else unassigned += r.total_kg;
    });
    return { demandByInk: map, allocByInk: alloc, jobsByInk: jobs, unassignedKg: unassigned };
  }, [demand?.inks]);

  // Merge: one row per stock SKU, plus any demand assigned to an ink that isn't
  // in the stock list (inactive/missing master) so demand is never silently dropped.
  const rows = useMemo<BalanceRow[]>(() => {
    const stock = stockQ.data?.stock ?? [];
    const seen = new Set<string>();
    const out: BalanceRow[] = [];

    const push = (s: InkStockRow, inStockMaster: boolean) => {
      const grossKg = demandByInk.get(s.ink_key) ?? 0;
      const allocatedKg = allocByInk.get(s.ink_key) ?? 0;
      // What is still to be covered. Kitting satisfies a requirement, so ink
      // already on an open trolley run comes off the demand (Jack, 11/09).
      const demandKg = Math.max(0, grossKg - allocatedKg);
      const onOrder = onOrderByInk[s.ink_key] ?? 0;
      // FREE stock only. Trolley stock is spoken for by the very run whose
      // demand we just netted off, so counting it here would credit the same
      // tubs twice. Both sides of the equation lose the kitted ink together,
      // which is what stops the balance swinging when a trolley is kitted or a
      // tub is opened at the press.
      const balance = s.store_kg + onOrder - demandKg;
      const reorder = s.reorder_point_kg ?? 0;
      out.push({
        ink_key: s.ink_key,
        description: s.description,
        colour_kind: s.colour_kind,
        hex_colour: s.hex_colour,
        material_class: s.material_class,
        series_suffix: s.series_suffix,
        series_name: s.series_name,
        manufacturer: s.manufacturer,
        ink_system: s.ink_system,
        series_hex: s.series_hex,
        low_migration: s.low_migration,
        supplier_code: s.supplier_code,
        reorder_point_kg: s.reorder_point_kg,
        store_kg: s.store_kg,
        trolley_kg: s.trolley_kg,
        on_hand_kg: s.on_hand_kg,
        stock_value: s.stock_value,
        on_order_kg: onOrder,
        demand_kg: demandKg,
        gross_demand_kg: grossKg,
        allocated_kg: allocatedKg,
        balance_kg: balance,
        shortfall: reorder - balance,
        in_stock_master: inStockMaster,
        src: s,
      });
    };

    stock.forEach((s) => { seen.add(s.ink_key); push(s, true); });

    // Demand assigned to an ink with no stock row — synthesize a zero-stock line.
    // Skip when the filter is coatings-only: demand is always ink-side.
    if (materialClass !== 'coating') (demand?.inks ?? []).forEach((r) => {
      if (!r.ink_key || seen.has(r.ink_key)) return;
      seen.add(r.ink_key);
      push({
        ink_key: r.ink_key,
        description: r.name,
        colour_name: r.name,
        colour_kind: r.kind,
        hex_colour: r.hex_colour,
        material_class: 'ink',
        active: true,
        series_suffix: r.series_suffix ?? '',
        series_name: r.series_name ?? '',
        manufacturer: r.manufacturer ?? '',
        ink_system: r.ink_system ?? '',
        low_migration: r.low_migration,
        series_hex: r.series_hex,
        supplier_code: null,
        supplier_article_no: null,
        pack_size_kg: null,
        standard_cost_per_kg: null,
        reorder_point_kg: null,
        store_kg: 0,
        trolley_kg: 0,
        on_hand_kg: 0,
        stock_value: 0,
        open_batches: 0,
        below_reorder_point: false,
      }, false);
    });

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockQ.data?.stock, demand?.inks, demandByInk, onOrderQ.data, materialClass]);

  // Distinct series present in the current rows, for the Series filter dropdown.
  const seriesOptions = useMemo(() => {
    const map = new Map<string, string>();   // suffix → label
    rows.forEach((r) => {
      if (!r.series_suffix) return;
      const label = [r.manufacturer, r.series_name, r.ink_system].filter(Boolean).join(' ').toUpperCase()
        || r.series_suffix;
      map.set(r.series_suffix, label);
    });
    return Array.from(map.entries()).map(([suffix, label]) => ({ suffix, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  // Filter + sort: worst shortfall first (the order list), then by key.
  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = rows;
    if (term) {
      list = list.filter((r) =>
        r.ink_key.toLowerCase().includes(term)
        || r.description?.toLowerCase().includes(term)
        || r.series_name?.toLowerCase().includes(term));
    }
    if (seriesFilter !== 'all') list = list.filter((r) => r.series_suffix === seriesFilter);
    if (shortfallOnly) list = list.filter((r) => r.shortfall > 0);
    return [...list].sort((a, b) => {
      if ((a.shortfall > 0) !== (b.shortfall > 0)) return a.shortfall > 0 ? -1 : 1;
      if (a.shortfall !== b.shortfall) return b.shortfall - a.shortfall;
      return a.ink_key.localeCompare(b.ink_key);
    });
  }, [rows, search, shortfallOnly, seriesFilter]);

  // Column totals run over the FILTERED rows, not every SKU, so they answer
  // "what am I looking at" rather than "what exists" — switch to Coatings and
  // the totals follow (Jack, 14/09).
  const totals = useMemo(() => visibleRows.reduce((t, r) => ({
    onHand: t.onHand + r.on_hand_kg,
    value: t.value + (r.in_stock_master ? Number(r.stock_value || 0) : 0),
    trolley: t.trolley + r.trolley_kg,
    onOrder: t.onOrder + r.on_order_kg,
    outstanding: t.outstanding + r.demand_kg,
  }), { onHand: 0, value: 0, trolley: 0, onOrder: 0, outstanding: 0 }), [visibleRows]);

  const totalDemand = rows.reduce((t, r) => t + r.demand_kg, 0);
  const totalAllocated = rows.reduce((t, r) => t + r.allocated_kg, 0);
  const shortCount = rows.filter((r) => r.shortfall > 0).length;
  const warming = warm.done < warm.total;
  const pending = demand?.pending_extraction ?? [];
  const freshPending = pending.filter((j) => !attempted.current.has(j.job_number)).length;
  const stuckPending = pending.length - freshPending;   // tried this session, still not cached

  const loading = stockQ.isLoading || demandQ.isLoading;
  const error = stockQ.error || demandQ.error;

  return (
    <Box sx={embedded ? { p: 0, maxWidth: 1500 } : { p: { xs: 1.5, md: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
          <BalanceRounded sx={{ color: TOKENS.accent }} />
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink balance</Typography>
        </Stack>
      )}
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2, maxWidth: 900 }}>
        On hand and demand merged per SKU: <b>On hand + On order − Demand = Balance</b>. A negative
        balance, or one below the reorder point, floats to the top — that’s your order list. On order
        counts outstanding ink on placed (issued) purchase orders.
      </Typography>

      {/* headline: total demand · SKUs short */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
        gap: 2, p: 2, mb: 2, borderRadius: '12px', bgcolor: TOKENS.lineSoft,
      }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
              Demand
            </Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: TOKENS.accent, lineHeight: 1.15 }}>
              {kg(totalDemand)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
              across all open jobs
              {totalAllocated > 0 && (
                <Box component="span" sx={{ color: TROLLEY_BLUE, fontWeight: 700 }}>
                  {' · '}{kg(totalAllocated)} already kitted
                </Box>
              )}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
              SKUs short
            </Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, color: shortCount ? TOKENS.bad : TOKENS.good }}>
              {shortCount}
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
              {shortCount ? 'need ordering' : 'all covered'}
            </Typography>
          </Box>
        </Stack>
        {/* The manual "Warm cache" button used to live here. Removed 14/09:
            warming is covered by two timers (nightly 10pm–1:30am, plus Sunday
            afternoons), so a manual trigger on the ordering screen was noise on
            a screen that answers "what do I have and need". The escape hatch
            moved to Artwork Health, which is where warm STATE already lives and
            where you go when a figure here looks wrong. */}
      </Box>


      {/* Passive demand: prompt to cost uncached jobs rather than auto-sweeping. */}
      {warming ? (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3, mb: 0.5 }}>
            Costing {warm.total - warm.done} more artwork
            {warm.total - warm.done === 1 ? '' : 's'} from Cloudflow (one at a time)…
          </Typography>
          <LinearProgress variant="determinate" value={warm.total ? (warm.done / warm.total) * 100 : 0} sx={{ borderRadius: 1 }} />
        </Box>
      ) : freshPending > 0 ? (
        <Alert
          severity="info" sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<BoltRounded />} onClick={warmPending}>
              Cost them now
            </Button>
          }
        >
          {freshPending} open job{freshPending === 1 ? '' : 's'} {freshPending === 1 ? 'isn’t' : 'aren’t'} costed yet —
          {' '}demand below excludes {freshPending === 1 ? 'it' : 'them'}. Costing reads each artwork from
          Cloudflow once and caches it (also used by Demand and the board).
        </Alert>
      ) : stuckPending > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {stuckPending} job{stuckPending === 1 ? '' : 's'} couldn’t be costed this session (artwork not found
          or not ready in Cloudflow) and {stuckPending === 1 ? 'is' : 'are'} not in the demand below.
        </Alert>
      ) : null}

      {/* filters + stock-entry actions */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <TextField size="small" placeholder="Search key / description / series…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
          sx={{ width: 280 }} />
        <ToggleButtonGroup size="small" exclusive value={materialClass}
          onChange={(_e, v: 'ink' | 'coating' | 'all' | null) => { if (v) setMaterialClass(v); }}>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
          <ToggleButton value="ink" sx={{ textTransform: 'none', px: 1.5 }}>Inks</ToggleButton>
          <ToggleButton value="coating" sx={{ textTransform: 'none', px: 1.5 }}>Coatings</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Series</InputLabel>
          <Select label="Series" value={seriesFilter} onChange={(e) => setSeriesFilter(String(e.target.value))}>
            <MenuItem value="all">All series</MenuItem>
            {seriesOptions.map((s) => (
              <MenuItem key={s.suffix} value={s.suffix}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch size="small" checked={shortfallOnly} onChange={(e) => setShortfallOnly(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>Shortfalls only</Typography>} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<AddRounded />}
          onClick={() => setOpeningOpen(true)}
          sx={{ color: TOKENS.accent, borderColor: `${TOKENS.accent}80` }}>
          Opening balance
        </Button>
        <Button size="small" variant="contained" startIcon={<AddRounded />}
          onClick={() => setReceiptOpen(true)}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Goods in
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }} spacing={1.5}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>Merging stock and demand…</Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error">Couldn’t load the balance report: {(error as Error)?.message ?? 'unknown error'}</Alert>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Ink', 'Description', 'Series'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
                <Tooltip title="Sealed tubs we hold, everywhere — store plus trolleys."><TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>On hand</TableCell></Tooltip>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Value</TableCell>
                <Tooltip title="Of the on-hand figure, what is out on a trolley — committed to an open run, so not free to cover anything else."><TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TROLLEY_BLUE }}>On trolley</TableCell></Tooltip>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>On order</TableCell>
                <Tooltip title="What still has to be bought: what the open jobs need, less anything already kitted onto a trolley run. The equation is at the foot of the screen."><TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Demand</TableCell></Tooltip>
                <Tooltip title="Free stock in the store, plus what is on order, less outstanding demand. Trolley stock is excluded — it is already spoken for."><TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Balance</TableCell></Tooltip>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Reorder pt</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => {
                const short = r.shortfall > 0;
                const negative = r.balance_kg < 0;
                const jobs = jobsByInk.get(r.ink_key) ?? [];
                const hasJobs = jobs.length > 0;
                const isOpen = expanded.has(r.ink_key);
                return (
                  <React.Fragment key={r.ink_key}>
                  <TableRow hover
                    onClick={() => r.in_stock_master && setDetailKey(r.ink_key)}
                    sx={{
                      cursor: r.in_stock_master ? 'pointer' : 'default',
                      bgcolor: negative ? 'rgba(220,38,38,0.05)' : short ? 'rgba(245,158,11,0.06)' : undefined,
                      '& > td:first-of-type': short
                        ? { boxShadow: `inset 3px 0 0 ${negative ? TOKENS.bad : TOKENS.warn}` } : undefined,
                    }}>
                    <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Swatch hex={r.hex_colour} coating={r.material_class === 'coating'} size={13} />
                        <Box component="span" sx={{ color: r.low_migration ? LMQ_RED : TOKENS.ink }}>{r.ink_key}</Box>
                        {r.low_migration && (
                          <Chip size="small" label="LMQ" sx={{ height: 16, fontSize: 9.5, fontWeight: 800, color: '#fff', bgcolor: LMQ_RED }} />
                        )}
                        {!r.in_stock_master && (
                          <Tooltip title="Assigned in demand but not in the ink stock master (inactive or missing) — shown so demand isn’t lost">
                            <Chip size="small" label="NO MASTER" sx={{ height: 16, fontSize: 9, fontWeight: 800, color: '#8a5200', bgcolor: 'rgba(178,106,0,0.15)' }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: r.low_migration ? LMQ_RED : TOKENS.ink2 }}>
                      {r.description?.toUpperCase() || '—'}
                    </TableCell>
                    <TableCell><SeriesTag row={r} /></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 12.5 }}>
                      {kg(r.on_hand_kg)}
                      <OwnerSplit consigned={r.src.consigned_kg} owned={r.src.owned_kg} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: TOKENS.ink2 }}>
                      {r.in_stock_master ? money(r.stock_value) : <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
                      {r.trolley_kg > 0 ? (
                        <Chip size="small" label={kg(r.trolley_kg)}
                          sx={{ height: 19, fontSize: 11, fontWeight: 700, color: TROLLEY_BLUE,
                            bgcolor: `${TROLLEY_BLUE}14`, border: `1px solid ${TROLLEY_BLUE}55` }} />
                      ) : <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: TOKENS.ink3 }}>
                      {r.on_order_kg > 0 ? kg(r.on_order_kg) : <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>}
                    </TableCell>
                    <TableCell align="right"
                      title={r.allocated_kg > 0
                        ? `${kg(r.gross_demand_kg)} needed by the open jobs, ${kg(r.allocated_kg)} already kitted onto a trolley run — only the remainder still has to be bought.`
                        : undefined}
                      sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: r.demand_kg > 0 ? TOKENS.ink2 : TOKENS.ink3, whiteSpace: 'nowrap' }}>
                      {hasJobs ? (
                        <Tooltip title={isOpen ? 'Hide the jobs' : 'Show the jobs driving this demand'}>
                          <Box component="span" onClick={(e) => { e.stopPropagation(); toggleExpanded(r.ink_key); }}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer',
                              '&:hover': { color: TOKENS.accent } }}>
                            <KeyboardArrowRightRounded sx={{ fontSize: 16, color: TOKENS.ink3,
                              transition: 'transform 120ms', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                            {kg(r.demand_kg)}
                          </Box>
                        </Tooltip>
                      ) : (r.demand_kg > 0 ? kg(r.demand_kg) : '—')}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: 13,
                      color: negative ? TOKENS.bad : short ? '#8a5200' : TOKENS.ink,
                    }}>
                      {kg(r.balance_kg)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, color: TOKENS.ink3 }}>
                      {r.reorder_point_kg == null ? '—' : kg(r.reorder_point_kg)}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {r.in_stock_master && (
                        <Tooltip title="Adjust (stock take, spoilage, spillage)">
                          <IconButton size="small" onClick={() => setAdjustRow(r.src)}>
                            <TuneRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {hasJobs && (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none', bgcolor: 'rgba(0,0,0,0.015)' }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.25, pl: 4, pr: 1 }}>
                            <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.5,
                              fontWeight: 800, color: TOKENS.ink3, mb: 0.5 }}>
                              Jobs driving demand — {jobs.length} job{jobs.length === 1 ? '' : 's'} · {kg(r.gross_demand_kg)}
                              {r.allocated_kg > 0 && (
                                <Box component="span" sx={{ color: TROLLEY_BLUE, textTransform: 'none', letterSpacing: 0 }}>
                                  {'  '}· {kg(r.allocated_kg)} of it already on a trolley
                                </Box>
                              )}
                            </Typography>
                            <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' }, maxWidth: 720 }}>
                              <TableHead>
                                <TableRow>
                                  {['Job', 'Customer', 'Item', 'Demand'].map((h, i) => (
                                    <TableCell key={h} align={i === 3 ? 'right' : 'left'}
                                      sx={{ fontWeight: 700, fontSize: 10.5, color: TOKENS.ink3 }}>{h}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {[...jobs].sort((a, b) => b.demand_kg - a.demand_kg).map((j) => (
                                  <TableRow key={j.job_number} hover>
                                    <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 12, fontWeight: 700 }}>{j.job_number}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: TOKENS.ink2 }}>{j.customer_code ?? '—'}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: TOKENS.ink3 }}>{j.customer_ref ?? '—'}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                                      {kg(j.demand_kg)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
              {/* Totals. Balance is deliberately NOT totalled: a positive on one
                  ink cannot cover a negative on another — they are different
                  products. Summing them would invent a number that reads like
                  reassurance. */}
              {visibleRows.length > 0 && (
                <TableRow sx={{ '& td': { borderTop: `2px solid ${TOKENS.line}`, borderBottom: 'none', py: 1.25 } }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 800, fontSize: 12 }}>
                    Total · {visibleRows.length} SKU{visibleRows.length === 1 ? '' : 's'} shown
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                    {kg(totals.onHand)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                    {money(totals.value)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12.5, color: TROLLEY_BLUE, fontVariantNumeric: 'tabular-nums' }}>
                    {totals.trolley > 0 ? kg(totals.trolley) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, color: TOKENS.ink3, fontVariantNumeric: 'tabular-nums' }}>
                    {totals.onOrder > 0 ? kg(totals.onOrder) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                    {totals.outstanding > 0 ? kg(totals.outstanding) : '—'}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              )}
              {visibleRows.length === 0 && (
                <TableRow><TableCell colSpan={11} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                  {shortfallOnly ? 'Nothing is short — every SKU covers its demand.' : 'No SKUs to show.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* The equation, stated. Jack, 14/09: "can you somehow state in the footer
          the exact balance equation the system now uses?" — worth having on the
          screen rather than in a doc, because the netting is the part people
          get wrong when they first read a zero in Outstanding. */}
      <Box sx={{
        mt: 2, p: 1.75, borderRadius: '10px',
        border: `1px solid ${TOKENS.line}`, bgcolor: TOKENS.lineSoft,
      }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5,
          textTransform: 'uppercase', color: TOKENS.ink3, mb: 0.75,
        }}>
          How these numbers relate
        </Typography>
        <Box sx={{
          fontFamily: TOKENS.monoFont, fontSize: 12.5, lineHeight: 1.9,
          color: TOKENS.ink, whiteSpace: 'pre-wrap',
        }}>
          <Box component="span" sx={{ fontWeight: 800 }}>Demand</Box>
          {'      =  gross demand  −  '}
          <Box component="span" sx={{ color: TROLLEY_BLUE, fontWeight: 700 }}>already kitted</Box>
          {'   (never below zero)\n'}
          <Box component="span" sx={{ fontWeight: 800, color: TOKENS.accent }}>Balance</Box>
          {'     =  free stock (STORE)  +  on order  −  Demand'}
        </Box>
        <Typography sx={{ fontSize: 11.5, color: TOKENS.ink2, mt: 1, lineHeight: 1.6 }}>
          <b>Gross demand</b> is what every open job needs, from the artwork coverage × run
          length × mileage — the <b>Demand</b> column shows what is LEFT of it to buy. <b>Already
          kitted</b> is ink sitting on an open trolley run: it has done its job, so it comes off. For the same reason <b>On trolley stock is
          not counted as cover</b>: it is committed to the run whose demand just came off, and
          counting it would credit the same tubs twice. Both sides lose the kitted ink together,
          which is why the balance doesn’t move when you kit a trolley or open a tub at the press.
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 0.75, lineHeight: 1.6 }}>
          A job whose trolley run has been <b>reconciled</b> stops asking for ink entirely — its
          demand leaves the gross figure, not just the outstanding one.
        </Typography>
      </Box>

      {/* unassignable demand — real demand we can't net against a SKU yet */}
      {unassignedKg > 0 && (
        <Accordion elevation={0} disableGutters sx={{
          mt: 2.5, border: '1px solid rgba(178,106,0,0.35)', borderRadius: '10px', '&:before': { display: 'none' },
          bgcolor: 'rgba(178,106,0,0.04)',
        }}>
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberRounded sx={{ color: '#b26a00', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: '#8a5200' }}>
                {kg(unassignedKg)} of demand isn’t assigned to an ink yet
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
              Some open jobs need a separation (e.g. a “cyan”) that hasn’t been mapped to a specific ink
              product. That demand is real but can’t be netted against a SKU’s balance until it’s assigned.
              Assign it in <b>Ink Assign</b> and it’ll roll into the right SKU here.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ledger modal + stock-entry dialogs, reused from the stock screen */}
      {detailKey && (
        <DetailDialog inkKey={detailKey} onClose={() => setDetailKey(null)}
          onAdjust={(row) => { setDetailKey(null); setAdjustRow(row); }} />
      )}
      {receiptOpen && (
        <LinesDialog mode="receipt" onClose={() => setReceiptOpen(false)}
          onSaved={() => { setReceiptOpen(false); invalidateLedger(); }} />
      )}
      {openingOpen && (
        <LinesDialog mode="opening" onClose={() => setOpeningOpen(false)}
          onSaved={() => { setOpeningOpen(false); invalidateLedger(); }} />
      )}
      {adjustRow && (
        <AdjustDialog row={adjustRow} onClose={() => setAdjustRow(null)}
          onSaved={() => { setAdjustRow(null); invalidateLedger(); }} />
      )}
    </Box>
  );
}
